// =============================================================================
// Skeleton loaders — пульсирующие плейсхолдеры формы будущего контента.
// Используются вместо <CenteredSpinner /> для loading state'ов основных
// user-страниц: ощущение мгновенного отклика, в отличие от пустого spinner'а.
//
// Tailwind animate-pulse (opacity 100→50→100, 2s loop) — нативная анимация,
// без JS таймеров, не блокирует рендер.
// =============================================================================

export function SkeletonLine({ className = '' }: { className?: string }) {
  return <div className={`bg-bg-elev2 animate-pulse ${className}`} />;
}

/**
 * Скелет одной MarketCard — повторяет shape: ribbon top + h3 + odds-двойняшка
 * + footer strip. Размеры в кратчайшем виде, без точного попадания в pixel.
 */
export function MarketCardSkeleton() {
  return (
    <div className="bg-bg-elev border border-line overflow-hidden">
      {/* Ribbon top */}
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-line">
        <SkeletonLine className="h-3 w-16" />
        <SkeletonLine className="h-3 w-10" />
        <span className="flex-1" />
        <SkeletonLine className="h-3 w-12" />
      </div>
      {/* Body — title + odds */}
      <div className="px-3.5 pt-3.5 pb-1">
        <SkeletonLine className="h-4 w-full mb-1.5" />
        <SkeletonLine className="h-4 w-3/4 mb-3" />
        <div className="grid grid-cols-2 gap-1.5">
          <SkeletonLine className="h-[68px]" />
          <SkeletonLine className="h-[68px]" />
        </div>
      </div>
      {/* Footer strip */}
      <div className="grid grid-cols-2 mt-3.5 border-t border-line">
        <div className="px-3.5 py-2 border-r border-line">
          <SkeletonLine className="h-2 w-8 mb-1" />
          <SkeletonLine className="h-3 w-16" />
        </div>
        <div className="px-3.5 py-2">
          <SkeletonLine className="h-2 w-12 mb-1" />
          <SkeletonLine className="h-3 w-10" />
        </div>
      </div>
    </div>
  );
}

/**
 * Скелет /markets feed — 3 MarketCardSkeleton + header / stat blocks placeholder.
 */
export function MarketsListSkeleton() {
  return (
    <div className="px-4 flex flex-col gap-2.5 pb-4">
      <MarketCardSkeleton />
      <MarketCardSkeleton />
      <MarketCardSkeleton />
    </div>
  );
}

/**
 * Скелет /market detail — top bar + tags + title + hero + odds + bet form.
 */
export function MarketDetailSkeleton() {
  return (
    <div className="pb-4">
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center gap-2">
        <SkeletonLine className="h-9 w-9 shrink-0" />
        <span className="flex-1" />
        <SkeletonLine className="h-9 w-9 shrink-0" />
        <SkeletonLine className="h-9 w-9 shrink-0" />
      </div>
      {/* Tags */}
      <div className="px-4 flex gap-1.5 mb-3">
        <SkeletonLine className="h-5 w-20" />
        <SkeletonLine className="h-5 w-12" />
        <SkeletonLine className="h-5 w-16" />
      </div>
      {/* Title */}
      <div className="px-4 mb-4">
        <SkeletonLine className="h-7 w-full mb-2" />
        <SkeletonLine className="h-7 w-2/3" />
      </div>
      {/* Hero placeholder */}
      <SkeletonLine className="w-full aspect-[16/9] mb-4" />
      {/* Odds */}
      <div className="px-4 grid grid-cols-2 gap-2 mb-4">
        <SkeletonLine className="h-[120px]" />
        <SkeletonLine className="h-[120px]" />
      </div>
      {/* Stats strip */}
      <div className="px-4 grid grid-cols-3 gap-1 mb-4">
        <SkeletonLine className="h-12" />
        <SkeletonLine className="h-12" />
        <SkeletonLine className="h-12" />
      </div>
      {/* Bet form */}
      <div className="px-4">
        <SkeletonLine className="h-[180px]" />
      </div>
    </div>
  );
}

/**
 * Скелет /profile — HeroCard + BalanceCard + 3-4 sub-секции.
 */
export function ProfileSkeleton() {
  return (
    <div className="px-4 pt-6 pb-6">
      {/* HeroCard */}
      <div className="flex items-center gap-3.5 mb-5">
        <SkeletonLine className="w-14 h-14 shrink-0" />
        <div className="flex-1">
          <SkeletonLine className="h-6 w-32 mb-1.5" />
          <SkeletonLine className="h-3 w-40 mb-1.5" />
          <SkeletonLine className="h-4 w-24" />
        </div>
      </div>
      {/* BalanceCard */}
      <SkeletonLine className="h-[200px] mb-4" />
      {/* Sub-sections */}
      <SkeletonLine className="h-24 mb-3" />
      <SkeletonLine className="h-24 mb-3" />
      <SkeletonLine className="h-24" />
    </div>
  );
}
